import React, { useCallback, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight } from '@fortawesome/pro-solid-svg-icons/faArrowRight';
import { useLang } from 'shared/hooks/lang.hooks';
import { resolveSearchAnswerImpression } from 'shared/panda/search-answer';
import type {
  PandaSearchAnswer as PandaSearchAnswerState,
  SearchAnswerImpression,
} from 'shared/panda/search-answer';
import { stripCitationMarkers } from 'shared/panda/messages/doc-answer';
import { ANALYTICS_SOURCES, EVENTS } from 'shared/constants/analytics';
import { PANDA_SEARCH_RESULT_TYPE } from 'shared/panda/panda.constants';
import { buildSearchImpressionProperties } from 'shared/helpers/src/search-impression-properties';
import navigationContextManager from 'shared/helpers/src/navigation-context-manager';
import { useAnalyticsImpression } from 'src/hooks/analytics/use-analytics-impression';
import { PandaInsightAvatar } from 'src/images';
import { PANDA_CSS_VARS } from '../panda-css-vars';
import { createPandaSearchAgent } from '../panda-chat-v2/panda-agent';
import { pandaAnalytics } from '../panda-chat-v2/panda-analytics';
import styles from './panda-search-answer.module.scss';
import { useCachedPandaSearchAnswer } from './use-cached-panda-search-answer';

interface PandaSearchAnswerProps {
  term: string;
  /** Rotates per submit, same term included — re-asks Panda on a re-search. */
  submitId?: string | null;
  /** The search page's session id — joins the card's events to the page's. */
  contextId?: string;
  /** The search session id the page reports on its clicked events. */
  sessionId?: string;
  /** Renders nothing while true; the run stays mounted and keeps streaming. */
  hidden?: boolean;
  /** True while answer text is on the page — the "no results" copy softens on it. */
  onHasAnswerChange?: (hasAnswer: boolean) => void;
}

/**
 * The Panda answer above the search results. Look and handoff only; the run
 * itself is `useCachedPandaSearchAnswer`. Renders nothing when Panda has nothing to say.
 */
export function PandaSearchAnswer({
  term,
  submitId = null,
  contextId,
  sessionId,
  hidden = false,
  onHasAnswerChange,
}: PandaSearchAnswerProps) {
  const submission = React.useMemo(() => ({ term, submitId }), [term, submitId]);
  const answer = useCachedPandaSearchAnswer({
    submission,
    contextId,
    createAgent: createPandaSearchAgent,
    analytics: pandaAnalytics,
    onHasAnswerChange,
  });

  // Resolved here, not in the card: it unmounts mid-context (hidden toggle, resubmit) and would re-fire.
  const impression = resolveSearchAnswerImpression(contextId);

  if (hidden || !answer.isVisible) return null;

  // The key remounts the card on contextId change so the tracker can fire for the new context.
  return (
    <AnswerSection
      key={contextId}
      answer={answer}
      term={term}
      contextId={contextId}
      sessionId={sessionId}
      impression={impression}
    />
  );
}

interface AnswerSectionProps {
  answer: PandaSearchAnswerState;
  term: string;
  contextId?: string;
  sessionId?: string;
  impression: SearchAnswerImpression;
}

function AnswerSection({ answer, term, contextId, sessionId, impression }: AnswerSectionProps) {
  const lang = useLang('PANDA_IN_SEARCH');
  const chatLang = useLang('AI_CHAT');

  const { cards, threadId, hasAnswer, loading, error, offersHubAnswer, showMore } = answer;

  // Not until answer text renders — the skeleton is not a seen response, so the observer
  // also waits for it: the 75% check must be against the answer, not a skeleton seen earlier.
  const reportable = hasAnswer && !!impression.impressionId && !impression.reported;
  const { ref: impressionRef } = useAnalyticsImpression({
    event: reportable ? EVENTS.SEARCH.RESULTS_IMPRESSION : null,
    inViewConfig: { skip: !reportable },
    eventPayload: buildSearchImpressionProperties(
      { id: threadId },
      PANDA_SEARCH_RESULT_TYPE,
      contextId,
    ),
    impressionId: impression.impressionId,
    onTrack: impression.markReported,
  });

  const handleShowMore = () => {
    const conversationId = showMore(offersHubAnswer ? { acceptOffer: true } : undefined);
    pandaAnalytics.trackAnswerClicked({
      term,
      searchSessionId: sessionId,
      contextId,
      impression_id: impression.impressionId,
      id: threadId,
      conversationId,
      resultType: PANDA_SEARCH_RESULT_TYPE,
    });
  };

  // Hover points analytics at the card; leaving must restore the page's own
  // context, since the route tracker only reruns on navigation.
  const previousContextRef = useRef<object | null>(null);

  const captureContext = () => {
    previousContextRef.current = navigationContextManager.getContext();
    navigationContextManager.setContext({
      source: ANALYTICS_SOURCES.SEARCH,
      sourceId: threadId,
    });
  };

  const restoreContext = useCallback(() => {
    navigationContextManager.setContext(previousContextRef.current ?? {});
    previousContextRef.current = null;
  }, []);

  // A run going HIDDEN unmounts the card with the pointer still inside it, so
  // `onMouseLeave` never fires and the card's context would stick to every
  // later event until the next route change.
  useEffect(
    () => () => {
      if (previousContextRef.current) restoreContext();
    },
    [restoreContext],
  );

  const isBeforeFirstToken = loading && cards.length === 0;
  const isClickable = !loading && !error;

  return (
    <section
      ref={impressionRef}
      className={`${styles.root} ${isClickable ? styles.clickable : ''}`}
      style={PANDA_CSS_VARS}
      aria-label={lang.INSIGHT_TITLE}
      aria-busy={loading}
      onMouseEnter={captureContext}
      onMouseLeave={restoreContext}
      onClick={isClickable ? handleShowMore : undefined}>
      {isBeforeFirstToken ? (
        <LoadingState label={lang.LOOKING_INTO_IT} />
      ) : (
        <>
          <img className={styles.avatar} src={PandaInsightAvatar} alt="" />

          <div className={styles.content}>
            <p className={styles.title}>{lang.INSIGHT_TITLE}</p>

            {error ? (
              <p className={styles.error}>{chatLang.ERROR_MESSAGE}</p>
            ) : offersHubAnswer ? (
              <p className={styles.suggestion}>{lang.ASA_SUGGESTION}</p>
            ) : (
              <div className={styles.body}>
                {cards.map(card => (
                  <React.Fragment key={card.key}>
                    {card.context && <p>{card.context}</p>}
                    <ReactMarkdown>{stripCitationMarkers(card.text)}</ReactMarkdown>
                  </React.Fragment>
                ))}
              </div>
            )}
          </div>

          {!loading && !error && (
            <button type="button" className={styles.showMore}>
              {offersHubAnswer ? lang.SHOW_ASA_GUIDELINES : lang.SHOW_MORE}
              <span className={styles.showMoreBadge}>
                <FontAwesomeIcon icon={faArrowRight} className={styles.showMoreIcon} />
              </span>
            </button>
          )}
        </>
      )}
    </section>
  );
}

function LoadingState({ label }: { label: string }) {
  return (
    <div className={styles.loading}>
      <p className={styles.loadingTitle}>{label}</p>
      <div className={styles.skeleton} aria-hidden="true">
        <span className={styles.skeletonAvatar} />
        <div className={styles.skeletonLines}>
          <span />
          <span />
          <span />
          <span />
        </div>
      </div>
    </div>
  );
}
